/**
 * The person's credits as text and decisions (CreditsNotice.jsx, PlanCompare.jsx), from the gateway's
 * `GET /api/v1/me/credits` (services/CreditsService.js, docs/billing.md "Me"). Nothing is invented: a figure the gateway
 * does not send (no cap, no spending yet) is not shown, and an estimate is called one.
 */
import { planText } from '../components/sandbox/planCompareText';

const num = (v) => typeof v === 'number' && Number.isFinite(v);
const decimal = (n) => Number(n).toLocaleString('en', { maximumFractionDigits: 1 });
const whole = (n) => Math.round(Number(n)).toLocaleString('en');

/** "85,892", "12.5", "−3". */
export function creditsAmount(value) {
  if (!num(value)) { return '—'; }
  const text = Math.abs(value) < 100 && !Number.isInteger(value) ? decimal(Math.abs(value)) : whole(Math.abs(value));
  return value < 0 && text !== '0' ? `−${text}` : text;
}

/** Whether the person's credits say anything at all: billing started and the gateway sent the balance. */
export function hasCredits(me) {
  return Boolean(me && me.started !== false && me.balance && num(me.balance.left));
}

/**
 * The line under "Your credits": what is left of the scope that runs out first, "of" what, and where (cap, team or
 * organization). No allowance ("of") for the organization's pool: just what is left.
 */
export function leftLine(me, lang) {
  const t = planText(lang);
  if (!hasCredits(me)) { return null; }
  const { scope, left, of } = me.balance;
  return t(num(of) ? 'creditsLeftOf' : 'creditsLeft', { left: creditsAmount(left), of: creditsAmount(of), where: t(`creditsScope_${scope}`) });
}

/** "42 spent this month · 5,219 a day · about 17 days left, an estimate", only what the gateway sent. */
export function spendLine(me, lang) {
  const t = planText(lang);
  if (!hasCredits(me)) { return null; }
  const parts = [];
  if (num(me.spentThisMonth?.credits)) { parts.push(t('creditsSpent', { n: creditsAmount(me.spentThisMonth.credits) })); }
  if (num(me.burnRate?.creditsPerDay) && me.burnRate.creditsPerDay > 0) { parts.push(t('creditsBurn', { n: creditsAmount(me.burnRate.creditsPerDay) })); }
  if (num(me.daysLeft?.days)) { parts.push(t('creditsDaysLeft', { n: Math.max(0, Math.round(me.daysLeft.days)) })); }
  return parts.length ? parts.join(' · ') : null;
}

/**
 * How long the credits would last if a plan ran all day, every day (its cost per hour at full reservation, an
 * estimate): `{ text }` for the Change plan table, or null where there is nothing to compare (no balance, no estimate
 * for the plan). Never a promise: the cost per hour is what the plan reserves, used or not.
 */
export function lastsCell(plan, me, lang) {
  const t = planText(lang);
  const perHour = plan?.costPerHour?.credits;
  if (!hasCredits(me) || !num(perHour) || perHour <= 0) { return null; }
  const left = me.balance.left;
  if (left <= 0) { return { text: t('lastsNone') }; }
  const days = left / (perHour * 24);
  if (days >= 365) { return { text: t('lastsOverYear') }; }
  if (days < 1) { return { text: t('lastsHours', { n: Math.max(1, Math.floor(days * 24)) }) }; }
  return { text: t('lastsDays', { n: Math.floor(days) }) };
}

/**
 * Chat's notice: `{ level: 'low' | 'blocked' | 'sleep', hold }` when the gateway has a warning for the person and Chat
 * is part of what they use. `hold`: credits are used up and the organization enforces it (new work waits until credits
 * are added), so Chat waits too: the gateway does not stop Chat itself, it only says so here. null: nothing to say.
 */
export function chatCreditsNotice(me) {
  if (!me || me.started === false || me.chat === false || !me.warning) { return null; }
  const level = me.warning.level;
  if (!['low', 'blocked', 'sleep'].includes(level)) { return null; }
  return { level, hold: level !== 'low' && Boolean(me.enforcement?.enabled), scope: me.warning.scope || me.balance?.scope || 'organization' };
}
